import React, { ReactNode, useCallback, useState } from "react";
import {
  Modal,
  TextInput,
  PasswordInput,
  Button,
  Anchor,
  Stack,
  Text,
  Box,
  Popover,
  Progress,
  Flex,
  Title,
  Tooltip,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { supabase } from "./supabaseClient";
import { IconCheck, IconInfoCircle, IconX } from "@tabler/icons-react";
import { deriveKey, getUserSettingsFromDb } from "./crypto-utils";
import { User } from "@supabase/supabase-js";

type AuthMode = "sign-in" | "sign-up" | "forgot-password";

export function AuthModal({
  opened,
  onClose,
  saveUserSettings,
  onLoadSettings,
  setIsFetchingSettings,
  onLoadDerivedKey,
}: {
  opened: boolean;
  onClose: () => void;
  saveUserSettings: (
    settings: object | null,
    sessionUser?: User | null,
  ) => Promise<void>;
  onLoadSettings: (settings: object) => void;
  setIsFetchingSettings: (isFetching: boolean) => void;
  onLoadDerivedKey: ({
    key,
    salt,
  }: {
    key: CryptoKey;
    salt: Uint8Array;
  }) => Promise<void>;
}) {
  const [mode, setMode] = useState<AuthMode>("sign-in");

  const form = useForm({
    initialValues: {
      email: "",
      password: "",
      confirmPassword: "",
    },

    validate: {
      email: (value) => (/^\S+@\S+$/.test(value) ? null : "Invalid email"),
      password: (value) =>
        mode !== "forgot-password" && value.length < 8
          ? "Password too short"
          : null,
      confirmPassword: (value, values) =>
        mode === "sign-up" && value !== values.password
          ? "Passwords do not match"
          : null,
    },
  });

  const handleClose = useCallback(() => {
    form.reset();
    setMode("sign-in");
    onClose();
  }, [form, onClose]);

  const handleSubmit = async () => {
    const { email, password } = form.values;

    if (mode === "sign-in") {
      await supabase.auth
        .signInWithPassword({
          email,
          password,
        })
        .then(({ data, error }) => {
          if (error) {
            form.setErrors({ email: error.message });
            return { error };
          }
          if (data.session) {
            // The user has signed in, here is where we can do
            // any password-related decryption and fetching, before we zero-out the password.
            const passwordBytes = new TextEncoder().encode(password);

            // Store a derived key for this session in the client-side context, using scrypt
            const p1 = deriveKey(passwordBytes)
              .then(onLoadDerivedKey)
              .then(() => {
                // Fetch user settings from the database, if they exist, and decrypt them
                // NOTE: This is the only time we can use the password to decrypt the settings.
                setIsFetchingSettings(true);
                return getUserSettingsFromDb(data.user, passwordBytes);
              })
              .then((settings) => {
                setIsFetchingSettings(false);
                if (settings && saveUserSettings) {
                  // We immediately re-save the settings to the database
                  // using the new session's salt and key. This allows us to only
                  // use the derived key for the rest of the session, i.e., if the user
                  // refreshes the browser tab, we can still fetch the settings and
                  // decrypt them for this session.
                  // NOTE: This uses the stored derivedKey in sessionStorage,
                  // so it must come after saving the derived key to sessionStorage.
                  saveUserSettings(settings, data.user);
                }
                return settings;
              })
              .then(onLoadSettings)
              .catch((error) => {
                console.error("Error fetching user settings:", error);
                setIsFetchingSettings(false);
                // Soft fail
              });

            Promise.allSettled([p1]).then(() => {
              passwordBytes.fill(0); // Zero out the password bytes
            });

            form.reset();
            handleClose();
          }
          return { error: new Error("Unknown error") };
        })
        .catch((error) => {
          form.setErrors({ email: error.message });
          return { error };
        });
    }

    if (mode === "sign-up") {
      const { error } = await supabase.auth.signUp({ email, password });
      if (error) form.setErrors({ email: error.message });
      else handleClose();
    }

    if (mode === "forgot-password") {
      const { error } = await supabase.auth.resetPasswordForEmail(email);
      if (error) form.setErrors({ email: error.message });
      else handleClose();
    }
  };

  const isSignUp = mode === "sign-up";
  const isForgot = mode === "forgot-password";

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={getTitle(mode)}
      centered
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack>
          <TextInput
            label="Email"
            placeholder="you@example.com"
            required
            {...form.getInputProps("email")}
          />

          {!isForgot && !isSignUp && (
            <PasswordInput
              label="Password"
              placeholder="Your password"
              required
              {...form.getInputProps("password")}
            />
          )}

          {isSignUp && !isForgot && (
            <PasswordStrengthInput
              value={form.values.password}
              setValue={(value) => form.setFieldValue("password", value)}
              props={form.getInputProps("password")}
            />
          )}

          {/* Confirm password only appears if user is typing password */}
          {isSignUp && form.values.password && (
            <PasswordInput
              label="Confirm Password"
              placeholder="Confirm your password"
              required
              {...form.getInputProps("confirmPassword")}
            />
          )}

          <Button type="submit" color="orange">
            {mode === "sign-in"
              ? "Sign In"
              : mode === "sign-up"
                ? "Create Account"
                : "Send Reset Link"}
          </Button>

          <Stack justify="space-between">
            {mode !== "forgot-password" && (
              <Anchor
                component="button"
                type="button"
                onClick={() => setMode("forgot-password")}
                size="sm"
                c="gray"
                underline
              >
                Forgot your password?
              </Anchor>
            )}
            {mode === "sign-in" && (
              <Anchor
                component="button"
                type="button"
                onClick={() => setMode("sign-up")}
                size="sm"
                c="gray"
                underline
              >
                Don&apos;t have an account? Register
              </Anchor>
            )}
            {mode === "sign-up" && (
              <Anchor
                component="button"
                type="button"
                onClick={() => setMode("sign-in")}
                size="sm"
                c="gray"
                underline
              >
                Already have an account? Sign In
              </Anchor>
            )}
            {mode === "forgot-password" && (
              <Anchor
                component="button"
                type="button"
                onClick={() => setMode("sign-in")}
                size="sm"
                c="gray"
                underline
              >
                Back to Sign In
              </Anchor>
            )}
          </Stack>
        </Stack>
      </form>
    </Modal>
  );
}

function getTitle(mode: AuthMode): string | ReactNode {
  switch (mode) {
    case "sign-in":
      return (
        <Flex align="center" justify="left">
          <Title mr="xs" order={3}>
            Login to <span className="cf-header">ChainForge.ai</span>
          </Title>
          <Tooltip
            label="Login to save your flows and securely store your API keys, so you don't have to set them every visit."
            multiline
            w="180px"
            withArrow
            position="right-start"
            withinPortal
          >
            <IconInfoCircle size={22} color="gray" />
          </Tooltip>
        </Flex>
      );
    case "sign-up":
      return (
        <Title mr="sm" order={3}>
          Create an Account
        </Title>
      );
    case "forgot-password":
      return (
        <Title mr="sm" order={3}>
          Reset your password
        </Title>
      );
    default:
      return "";
  }
}

/** Adapted from Mantine v6 Docs */
function PasswordRequirement({
  meets,
  label,
}: {
  meets: boolean;
  label: string;
}) {
  return (
    <Text
      color={meets ? "teal" : "red"}
      sx={{ display: "flex", alignItems: "center" }}
      mt={7}
      size="sm"
    >
      {meets ? <IconCheck size="0.9rem" /> : <IconX size="0.9rem" />}{" "}
      <Box ml={10}>{label}</Box>
    </Text>
  );
}

const requirements = [
  { re: /[0-9]/, label: "Includes number" },
  { re: /[a-z]/, label: "Includes lowercase letter" },
  { re: /[A-Z]/, label: "Includes uppercase letter" },
  { re: /[$&+,:;=?@#|'<>.^*()%!-]/, label: "Includes special symbol" },
];

function getStrength(password: string) {
  let multiplier = password.length > 5 ? 0 : 1;

  requirements.forEach((requirement) => {
    if (!requirement.re.test(password)) {
      multiplier += 1;
    }
  });

  return Math.max(100 - (100 / (requirements.length + 1)) * multiplier, 10);
}

function PasswordStrengthInput({
  value,
  setValue,
  props,
}: {
  value: string;
  setValue: (value: string) => void;
  props: any;
}) {
  const [popoverOpened, setPopoverOpened] = useState(false);
  const checks = requirements.map((requirement, index) => (
    <PasswordRequirement
      key={index}
      label={requirement.label}
      meets={requirement.re.test(value)}
    />
  ));

  const strength = getStrength(value);
  const color = strength === 100 ? "teal" : strength > 50 ? "yellow" : "red";

  return (
    <Box w="100%" mx="auto">
      <Popover
        opened={popoverOpened}
        position="bottom"
        width="target"
        transitionProps={{ transition: "pop" }}
        withinPortal
      >
        <Popover.Target>
          <div
            onFocusCapture={() => setPopoverOpened(true)}
            onBlurCapture={() => setPopoverOpened(false)}
          >
            <PasswordInput
              withAsterisk
              label="Your password"
              placeholder="Your password"
              value={value}
              onChange={(event) => setValue(event.currentTarget.value)}
              required
              {...props}
            />
            <Progress color={color} value={strength} size={5} mb="xs" />
          </div>
        </Popover.Target>
        <Popover.Dropdown>
          <PasswordRequirement
            label="Includes at least 8 characters"
            meets={value.length >= 8}
          />
          {checks}
        </Popover.Dropdown>
      </Popover>
    </Box>
  );
}
